// src/components/ConfirmDialog.jsx
"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Generic confirmation dialog.
 *
 * Props:
 * - open: boolean
 * - title?: string
 * - message?: string | ReactNode
 * - confirmLabel?: string (default: "Confirm")
 * - cancelLabel?: string (default: "Cancel")
 * - tone?: "default" | "danger" (accent color for confirm)
 * - confirming?: boolean (disables buttons + shows "…")
 * - onConfirm: () => void | Promise<void>
 * - onCancel: () => void
 * - autoFocus?: "confirm" | "cancel" (default: "cancel")
 */
export default function ConfirmDialog({
  open,
  title = "Confirm",
  message = "Are you sure?",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  confirming = false,
  onConfirm,
  onCancel,
  autoFocus = "cancel",
}) {
  const [mounted, setMounted] = useState(false);
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Focus management when opening
  useEffect(() => {
    if (!open) return;
    const el = autoFocus === "confirm" ? confirmRef.current : cancelRef.current;
    try { el?.focus(); } catch {}
  }, [open, autoFocus]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !confirming) onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, confirming, onCancel]);

  if (!open) return null;
  if (!mounted) return null;

  const confirmClasses =
    tone === "danger"
      ? "bg-red-600 text-white hover:opacity-90 focus-visible:ring-red-500"
      : "bg-[var(--brand)] text-white hover:opacity-90 focus-visible:ring-[var(--brand)]";

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-[var(--border)] bg-[var(--card)] p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-base font-semibold mb-2">{title}</div>
        <div className="text-sm opacity-80 mb-4">{message}</div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            ref={cancelRef}
            className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]"
            onClick={onCancel}
            disabled={confirming}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            ref={confirmRef}
            className={`rounded-lg px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60 ${confirmClasses}`}
            onClick={onConfirm}
            disabled={confirming}
          >
            {confirming ? `${confirmLabel}…` : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
